import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DatePickerComponent } from '../../../../shared/date-picker/date-picker.component';
import { ReportParameterBase } from './report-parameter-base';

export interface DatePickerParameterOptions {
    minDate?: Date;
    minDateParameterKey?: string;
    maxDate?: Date;
    maxDateParameterKey?: string;
}

@Component({
    selector: 'app-date-picker-parameter',
    imports: [FormsModule, DatePickerComponent],
    template: `
        <app-date-picker
            [selectedDate]="selectedDate()"
            [id]="parameterKey()"
            [placeholder]="parameterDefinition().placeholder"
            [disabled]="isDisabled()"
            [invalid]="isInvalid()"
            [minDate]="minDate()"
            [maxDate]="maxDate()"
            (selectedDateChange)="onSelectedDateChange($event)"
        />
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: DatePickerParameterComponent,
            multi: true
        }
    ]
})
export class DatePickerParameterComponent extends ReportParameterBase<unknown, DatePickerParameterOptions> {
    selectedDate = signal<Date | null>(null);

    parameterValues = this.reportWorkspace.parameterValues;

    minDate = computed(() => {
        const minDateOption = this.parameterOptions().minDate;

        const minDateParameterKey = this.parameterOptions().minDateParameterKey;
        const minDateParameter = minDateParameterKey ? this.parameterValues()[minDateParameterKey] : null;
        
        if (minDateOption == null || minDateParameter == null) {
            return minDateOption ?? minDateParameter;
        }

        return minDateOption > minDateParameter ? minDateOption : minDateParameter;
    });

    maxDate = computed(() => {
        const maxDateOption = this.parameterOptions().maxDate;

        const maxDateParameterKey = this.parameterOptions().maxDateParameterKey;
        const maxDateParameter = maxDateParameterKey ? this.parameterValues()[maxDateParameterKey] : null;

        if (maxDateOption == null || maxDateParameter == null) {
            return maxDateOption ?? maxDateParameter;
        }

        return maxDateOption < maxDateParameter ? maxDateOption : maxDateParameter;
    });

    onSelectedDateChange(selectedDate: Date | null | undefined) {
        const newDate = selectedDate ?? null;
        this.selectedDate.set(newDate);

        this.reportWorkspace.setParam(this.parameterKey(), newDate);

        this.onChange(newDate);
        this.markTouchedIfNotResetting();
    }

    // CVA callbacks
    writeValue(obj: Date | null): void {
        this.selectedDate.set(obj);
    }
}
